import { MATHEMATICS_BONUS_PER_LEVEL, MATHEMATICS_MAX_LEVEL, ROBOT_FACILITY_BONUS_PER_LEVEL, ROBOT_MAX_PER_RESOURCE, ROBOT_METAL_BONUS_PER_UNIT, ROBOT_MINERAL_BONUS_PER_UNIT, ROBOT_GAS_BONUS_PER_UNIT, DEFAULT_RESOURCE_RATES, } from "@kaeliux/shared";
import { BUILDING_DEFINITIONS } from "@kaeliux/shared";
import { growByBreakpoints } from "./buildings.js";
import { DEFAULT_STORAGE_CAPACITY } from "./resourceEngine.js";
/**
 * Mathematics additive multiplier — +5% per level, maximum +50% at level 10.
 * Applies only to Economic (metal/minerals/gas) output of resource-zone
 * buildings, not to Energy or Population — see `isEconomicResourceBuilding`.
 */
export function calculateMathematicsMultiplier(mathematicsLevel) {
    const level = Math.min(Math.max(Math.floor(mathematicsLevel), 0), MATHEMATICS_MAX_LEVEL);
    return 1 + level * MATHEMATICS_BONUS_PER_LEVEL;
}
const ECONOMIC_PRODUCTION_KEYS = ["metal", "minerals", "gas"];
/**
 * Total robots available from Engineering Facility (1/level) + Automation Center
 * (`ROBOT_FACILITY_BONUS_PER_LEVEL`/level) — see the constants' doc comment
 * in `packages/shared/src/constants/resources.ts` for the source formula.
 */
export function calculateTotalRobots(robotFactoryLevel, automationCenterLevel) {
    return Math.max(0, robotFactoryLevel) + Math.max(0, automationCenterLevel) * ROBOT_FACILITY_BONUS_PER_LEVEL;
}
/**
 * Split `totalRobots` evenly across Metal/Minerals/Gas, each capped at
 * `ROBOT_MAX_PER_RESOURCE` — there is no player-assignable UI (see issue
 * #56 discussion), so the split is deterministic: base = floor(total / 3)
 * per resource, then the remainder (0-2 robots) goes to Metal, then
 * Minerals, then Gas, one each, while that resource is still under its cap.
 * At the maximum (30 = 20 Engineering Facility + 2×5 Automation Center) every resource
 * lands exactly at its 10-robot cap with no remainder.
 */
export function distributeRobotsEvenly(totalRobots) {
    const capped = Math.max(0, Math.min(Math.floor(totalRobots), ROBOT_MAX_PER_RESOURCE * 3));
    const base = Math.floor(capped / 3);
    let remainder = capped - base * 3;
    const result = { metal: base, minerals: base, gas: base };
    for (const key of ["metal", "minerals", "gas"]) {
        if (remainder <= 0)
            break;
        if (result[key] < ROBOT_MAX_PER_RESOURCE) {
            result[key] += 1;
            remainder -= 1;
        }
    }
    return result;
}
/**
 * Per-resource production multiplier from an (already-capped, see
 * {@link distributeRobotsEvenly}) robot distribution — 6%/5%/4% per robot
 * for Metal/Minerals/Gas respectively (issue #56). Each count is
 * independently clamped to `[0, ROBOT_MAX_PER_RESOURCE]` so a caller
 * passing a raw (unclamped) distribution can't exceed the intended max
 * bonus.
 */
export function calculateRobotsMultipliers(robots) {
    const clamp = (n) => Math.min(Math.max(Math.floor(n), 0), ROBOT_MAX_PER_RESOURCE);
    return {
        metal: 1 + clamp(robots.metal) * ROBOT_METAL_BONUS_PER_UNIT,
        minerals: 1 + clamp(robots.minerals) * ROBOT_MINERAL_BONUS_PER_UNIT,
        gas: 1 + clamp(robots.gas) * ROBOT_GAS_BONUS_PER_UNIT,
    };
}
/**
 * Resource-zone buildings that produce an Economic resource (metal, minerals,
 * or gas) — the set Mathematics' bonus applies to per the docs ("Economic
 * resources gained from the buildings in the Resource zone"). Excludes Solar
 * Plant / Fusion Plant (zone "resource" but produce Energy, not Economic) and
 * Launcher (zone "resource", no resource production).
 */
function isEconomicResourceBuilding(buildingType) {
    const building = BUILDING_DEFINITIONS[buildingType];
    if (!building || building.zone !== "resource" || !building.production)
        return false;
    return ECONOMIC_PRODUCTION_KEYS.some((key) => building.production[key] !== undefined);
}
/**
 * Building's own base production at `level` — `baseProduction(building,
 * level)` grown by the building's own `productionMultipliers` curve, with NO
 * external multiplier applied. This is the first half of the shared
 * pipeline (base → curve → external multiplier); callers layer whatever
 * external multiplier applies to them (Mathematics for Economic resources,
 * environmental/Physics for Energy — see `calculatePlanetTick` in
 * resourceEngine.ts) on top of this as a separate step.
 *
 * Economic resource buildings (Poly-Carbon Extractor / Crystals Mine / Gas
 * Refinery) floor after every intermediate level, same convention as
 * Solar/Fusion Plant — this is what the issue #103 design numbers (e.g.
 * Poly-Carbon Extractor L50 = 31,880/hr with no external multipliers) were computed
 * against. Everything else (e.g. Launcher) still compounds at full precision
 * and rounds once at the end, since flooring every level would drift it off
 * the verified design spreadsheet values (~1.2% by level 20).
 *
 * Buildings without `productionMultipliers` fall back to the legacy
 * `base × level^1.5` curve so non-resource buildings keep working.
 *
 * @param buildingType - The type of building producing the resource
 * @param level - The level of the building
 * @returns The pre-external-multiplier production rate per hour
 */
export function calculateBuildingBaseProduction(buildingType, level) {
    if (level <= 0)
        return 0;
    const building = BUILDING_DEFINITIONS[buildingType];
    if (!building)
        return 0;
    // Base output at level 1: prefer the explicit field, otherwise derive it
    // from the legacy production map.
    const base = building.baseProduction ??
        building.production?.metal ??
        building.production?.minerals ??
        building.production?.gas ??
        building.production?.energy ??
        0;
    if (base === 0)
        return 0;
    // No breakpoint data: keep the legacy power curve.
    if (!building.productionMultipliers || building.productionMultipliers.length === 0) {
        return base * Math.pow(level, 1.5);
    }
    return growByBreakpoints(base, building.productionMultipliers, level, isEconomicResourceBuilding(buildingType));
}
/**
 * Calculate resource production rate for a specific resource: the building's
 * own base production ({@link calculateBuildingBaseProduction}) with the
 * Mathematics and Robots external multipliers applied as a separate final
 * step, rounded once.
 *
 *   result = round(calculateBuildingBaseProduction(type, level) × mathematicsMultiplier × robotMultiplier)
 *
 * @param buildingType - The type of building producing the resource
 * @param level - The level of the building
 * @param mathematicsMultiplier - From `calculateMathematicsMultiplier`; only
 *   applied to Economic resource-zone buildings (see
 *   `isEconomicResourceBuilding`), defaults to 1 (no bonus)
 * @param robotsMultipliers - From `calculateRobotsMultipliers`; the entry for
 *   this building's own resource (metal/minerals/gas) is applied, same
 *   Economic-only restriction as Mathematics, defaults to 1 (no bonus)
 * @returns The production rate per hour
 */
export function calculateResourceProduction(buildingType, level, mathematicsMultiplier = 1, robotsMultipliers = { metal: 1, minerals: 1, gas: 1 }) {
    const production = calculateBuildingBaseProduction(buildingType, level);
    if (production === 0)
        return 0;
    if (!isEconomicResourceBuilding(buildingType)) {
        return Math.round(production);
    }
    const building = BUILDING_DEFINITIONS[buildingType];
    const resourceKey = ECONOMIC_PRODUCTION_KEYS.find((key) => building.production?.[key] !== undefined);
    const robotMult = resourceKey ? robotsMultipliers[resourceKey] : 1;
    return Math.round(production * mathematicsMultiplier * robotMult);
}
/**
 * Calculate total resource production rates for all resources.
 *
 * `energy` is always 0 here — this function has no access to sun age, ozone,
 * or Physics level, so it structurally cannot produce a correct energy
 * number. `calculatePlanetTick` (resourceEngine.ts) is the sole source of
 * truth for energy — see the Energy section of
 * `docs/game-details/resources.md`. This does *not* mean planets go without
 * baseline energy: `calculatePlanetTick` guarantees a non-zero energy income
 * from sun age/proximity/ozone alone even with zero buildings.
 *
 * Robots (issue #56) are derived entirely from `buildings.robotFactory` /
 * `buildings.automationCenter` — there is no player-assignable distribution,
 * so the total ({@link calculateTotalRobots}) is always split evenly
 * ({@link distributeRobotsEvenly}) before its multiplier
 * ({@link calculateRobotsMultipliers}) is applied, same Economic-only
 * restriction as Mathematics.
 *
 * @param buildings - Map of building types to their levels
 * @param mathematicsLevel - Player's Mathematics science level (0 = no bonus)
 * @returns Object with production rates for each resource
 */
export function calculateTotalProduction(buildings, mathematicsLevel = 0) {
    const rates = {
        ...DEFAULT_RESOURCE_RATES,
        energy: 0,
        ozone: 0,
    };
    const mathMult = calculateMathematicsMultiplier(mathematicsLevel);
    const totalRobots = calculateTotalRobots(buildings.robotFactory ?? 0, buildings.automationCenter ?? 0);
    const robotsMult = calculateRobotsMultipliers(distributeRobotsEvenly(totalRobots));
    // Calculate production from each building
    for (const [buildingType, level] of Object.entries(buildings)) {
        const production = calculateResourceProduction(buildingType, level, mathMult, robotsMult);
        const building = BUILDING_DEFINITIONS[buildingType];
        if (building && building.production) {
            if (building.production.metal)
                rates.metal += production;
            if (building.production.minerals)
                rates.minerals += production;
            if (building.production.gas)
                rates.gas += production;
        }
    }
    return rates;
}
/**
 * Population capacity every planet has regardless of Launcher level — the
 * same "base + building bonus" shape as resource/energy income.
 */
export const DEFAULT_POPULATION_CAPACITY = 20;
/**
 * Calculate the Population capacity provided by the Launcher building.
 *
 * Every planet carries {@link DEFAULT_POPULATION_CAPACITY} regardless of the
 * Launcher level. The Launcher add a bonus on top of that, driven by the same
 * baseProduction/productionMultipliers curve — and the same
 * {@link calculateResourceProduction} formula — used for resource buildings
 * like the Poly-Carbon Extractor.
 *
 * @param hangarsLevel - Current level of the Launcher building (0 = not built)
 * @returns Population capacity (default capacity + Launcher bonus)
 */
export function calculatePopulationCapacity(hangarsLevel) {
    return DEFAULT_POPULATION_CAPACITY + calculateResourceProduction("hangars", hangarsLevel);
}
/**
 * Calculate storage capacity for a resource.
 *
 * Level 0 (no storage building) returns the planet's built-in base capacity;
 * every subsequent level multiplies by 1.5.
 *
 * @param storageLevel - Level of the storage building
 * @returns Maximum storage capacity
 */
export function calculateStorageCapacity(type, storageLevel) {
    if (storageLevel <= 0)
        return DEFAULT_STORAGE_CAPACITY;
    let multiplier = 1.5;
    switch (type) {
        case 'metalStorage':
            multiplier = 1.42;
            break;
        case 'mineralStorage':
            multiplier = 1.4;
            break;
        case 'gasStorage':
            multiplier = 1.38;
            break;
    }
    return Math.floor(DEFAULT_STORAGE_CAPACITY * Math.pow(multiplier, storageLevel));
}
//# sourceMappingURL=resources.js.map